/**
 * src/api/dbview.ts
 *
 * Typed wrappers for /v1/observe/dbview/* — the DB-viewer feature that
 * lets us inspect Mongo collections directly from observe.otrtech.co.uk
 * instead of SSH'ing onto the box. Mirror the shapes returned by
 * backend/product/src/modules/observe/dbview.repository.ts.
 */

import { request, getApiKey, getBaseUrl } from './client';

// ── Shared types (kept inline — only used here + DbView page) ────────────────

export interface CollectionStat {
  name: string;
  group: string;
  count: number;
  last_24h?: number;
}

export interface ViewableCollectionMeta {
  name: string;
  group: string;
  tsField: string | null;
}

export interface OverviewResp {
  ok: true;
  stats: CollectionStat[];
  collections: ViewableCollectionMeta[];
}

export interface PeekResp {
  ok: true;
  name: string;
  total: number;
  docs: Array<Record<string, unknown>>;
}

export interface ScrapedListItem {
  _id: string;
  website_name?: string;
  search_term?: string;
  page?: number;
  timestamp?: string;
  data_hash?: string;
  processed?: boolean;
}

export interface ScrapedListResp {
  ok: true;
  /** Null when the count was skipped (search filter — no usable index pre-007). */
  total: number | null;
  /** Echoed from the request so the UI can paginate without state drift. */
  skip: number;
  limit: number;
  /** True when there's at least one more page (cheaper than total). */
  hasMore: boolean;
  websites: Array<{ website_name: string; count: number }>;
  docs: ScrapedListItem[];
}

export interface FlowRow {
  source: string;
  scraped_24h: number;
  deals_24h: number;
  emitted_24h: number;
  normalize_ratio: number | null;
  emit_ratio: number | null;
}

export interface FlowResp {
  ok: true;
  since: string;
  totals: { scraped_24h: number; deals_24h: number; emitted_24h: number };
  rows: FlowRow[];
}

export interface LineageNode {
  collection: string;
  count: number;
  sample_ids: string[];
}

export interface LineageEdge {
  from: string;
  to: string;
  via: string;
}

export interface LineageResp {
  ok: true;
  anchor: { type: 'website' | 'unique_id'; value: string };
  nodes: LineageNode[];
  edges: LineageEdge[];
  notes: string[];
}

// ── BI data extract (Option A) ───────────────────────────────────────────────

export interface BiExtractFile {
  name: string;
  bytes: number;
  contentType: string;
}

export interface BiExtractResp {
  ok: true;
  available: boolean;
  built_at?: string;
  window_days?: number;
  rows?: Record<string, number>;
  files: BiExtractFile[];
}

export interface AggregateResp {
  ok: true;
  collection: string;
  rowCount: number;
  rows: Array<Record<string, unknown>>;
  capped: boolean;
}

// ── Calls ───────────────────────────────────────────────────────────────────

export function getOverview() {
  return request<OverviewResp>('/v1/observe/dbview/overview');
}

/** List the day-fresh BI extract files currently published. */
export function getBiExtract() {
  return request<BiExtractResp>('/v1/observe/dbview/bi-extract');
}

// ── Source health (D6 — silent scraper/cleaner degradation watcher) ──────────

export type SourceHealthStatus = 'ok' | 'warn' | 'critical';

export interface SourceHealthMetrics {
  evals_24h: number;
  evals_base_daily: number;
  yield_ratio: number;
  modelkey_pct_24h: number;
  modelkey_pct_base: number;
  unverified_share_24h: number;
  unverified_share_base: number;
  block_rate: number;
}

export interface SourceHealthRow {
  source: string;
  status: SourceHealthStatus;
  reasons: string[];
  metrics: SourceHealthMetrics;
}

export interface SourceHealthResp {
  ok: true;
  generatedAt: string;
  sources: SourceHealthRow[];
}

/** Per-source degradation report (recent 24h vs trailing 7-day baseline). */
export function getSourceHealth() {
  return request<SourceHealthResp>('/v1/observe/dbview/source-health');
}

/** Download one published extract file, preserving x-api-key auth (the file
 *  is gzip/binary, so we fetch a Blob rather than text). */
export async function downloadBiExtractFile(name: string): Promise<void> {
  const key = getApiKey();
  const res = await fetch(`${getBaseUrl()}/v1/observe/dbview/bi-extract/${encodeURIComponent(name)}`, {
    headers: key ? { 'x-api-key': key } : {},
  });
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Run a safe read-only aggregation (Option C). Validated + capped server-side. */
export function runAggregate(collection: string, pipeline: unknown) {
  return request<AggregateResp>('/v1/observe/dbview/aggregate', {
    method: 'POST',
    body: { collection, pipeline },
  });
}

export function peekCollection(name: string, opts: { limit?: number; filter?: Record<string, string> } = {}) {
  const q = new URLSearchParams();
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.filter) {
    for (const [k, v] of Object.entries(opts.filter)) {
      if (v !== undefined && v !== '') q.set(k, v);
    }
  }
  const qs = q.toString();
  return request<PeekResp>(
    `/v1/observe/dbview/collection/${encodeURIComponent(name)}${qs ? '?' + qs : ''}`,
  );
}

export function listScraped(opts: {
  website?: string; search?: string; limit?: number; skip?: number;
} = {}) {
  const q = new URLSearchParams();
  if (opts.website) q.set('website', opts.website);
  if (opts.search)  q.set('search', opts.search);
  if (opts.limit)   q.set('limit', String(opts.limit));
  if (opts.skip)    q.set('skip',  String(opts.skip));
  const qs = q.toString();
  return request<ScrapedListResp>(`/v1/observe/dbview/scraped${qs ? '?' + qs : ''}`);
}

export function getScrapedDoc(id: string) {
  return request<{ ok: true; doc: Record<string, unknown> }>(
    `/v1/observe/dbview/scraped/${encodeURIComponent(id)}`,
  );
}

export function getFlow24h() {
  return request<FlowResp>('/v1/observe/dbview/flow24h');
}

export function getLineage(opts: { website?: string; unique_id?: string }) {
  const q = new URLSearchParams();
  if (opts.website)   q.set('website', opts.website);
  if (opts.unique_id) q.set('unique_id', opts.unique_id);
  return request<LineageResp>(`/v1/observe/dbview/lineage?${q.toString()}`);
}

// ── Trends / KPIs ───────────────────────────────────────────────────────────

export interface TimeSeriesPoint { hour: string; n: number }
export interface KpiDelta {
  current_24h: number;
  prev_24h:    number;
  delta_pct:   number | null;
}
export interface TrendsResp {
  ok: true;
  since: string;
  series: {
    scraped: TimeSeriesPoint[];
    deals:   TimeSeriesPoint[];
    emitted: TimeSeriesPoint[];
    alerts:  TimeSeriesPoint[];
  };
  kpis: {
    scraped: KpiDelta;
    deals:   KpiDelta;
    emitted: KpiDelta;
    alerts:  KpiDelta;
  };
  top_sources: Array<{ source: string; n: number }>;
  scraper_status: Array<{ status: string; n: number }>;
  alerts_by_severity: Array<{ severity: string; n: number }>;
  freshness: Array<{
    id: string;
    last_run_at: string | null;
    hours_since: number | null;
    status: string | null;
  }>;
}

export function getTrends() {
  return request<TrendsResp>('/v1/observe/dbview/trends');
}

// ── Deals health ────────────────────────────────────────────────────────────

export interface DealsHealthRow {
  source: string;
  total: number;
  live: number;
  expired: number;
  suspended: number;
  out_of_stock: number;
  good: number;
  ok: number;
  bad: number;
  avg_price: number | null;
  min_price: number | null;
  max_price: number | null;
}

export interface DealsHealthResp {
  ok: true;
  rows: DealsHealthRow[];
  totals: { total: number; live: number; expired: number; suspended: number; out_of_stock: number };
  top_categories: Array<{ category: string; n: number }>;
}

export function getDealsHealth() {
  return request<DealsHealthResp>('/v1/observe/dbview/deals-health');
}

// ── Alerts heatmap ──────────────────────────────────────────────────────────

export interface HeatmapRow {
  category: string;
  cells: number[];
  total: number;
  max_severity: 'info' | 'warn' | 'error' | 'critical' | null;
}
export interface HeatmapResp {
  ok: true;
  since: string;
  hours: string[];
  rows: HeatmapRow[];
  max_cell: number;
}

export function getAlertsHeatmap() {
  return request<HeatmapResp>('/v1/observe/dbview/alerts-heatmap');
}

// ── Pipeline (Tab 1) ────────────────────────────────────────────────────────

export interface FunnelStage {
  name: string;
  count: number;
  drop_off_pct: number | null;
  /** 2026-05-10 — Tony calibration #4. Optional EMIT-only breakdown by
   *  dealTier (≥40 → exceptional / 25-39 → strong / <25 → standard).
   *  Populated only when the funnel was queried with `byDealTier: true`.
   *  Counts sum to the stage `count`. */
  by_deal_tier?: { exceptional: number; strong: number; standard: number };
}
export interface FunnelResp { ok: true; since: string; stages: FunnelStage[] }

export interface DecisionsResp {
  ok: true;
  scope: 'today' | 'all_active';
  /** When the request includes `groupByCategory=1`, each cell also has
   *  a `category` field. Existing callers that don't request grouping
   *  see the original (source, decision, n) shape unchanged. */
  cells: Array<{ source: string; decision: string; n: number; category?: string }>;
}

export interface DropReasonsResp {
  ok: true;
  since: string;
  sources: string[];
  rows: Array<{ reason: string; cells: number[]; total: number }>;
  max_cell: number;
}

export interface PipelineSparklineResp {
  ok: true;
  since: string;
  bucketHours: number;
  hours: string[];
  emit: number[];
  drop: number[];
  hold: number[];
}

export function getPipelineFunnel(opts: { sinceMs?: number; byDealTier?: boolean } = {}) {
  const q = new URLSearchParams();
  if (opts.sinceMs) q.set('sinceMs', String(opts.sinceMs));
  // 2026-05-10 — opt-in flag triggers the EMIT-by-dealTier $lookup on
  // the server. Default off so unrelated callers stay on the cheap path.
  if (opts.byDealTier) q.set('byDealTier', '1');
  const qs = q.toString();
  return request<FunnelResp>(`/v1/observe/dbview/pipeline/funnel${qs ? '?' + qs : ''}`);
}

export function getPipelineDecisions(opts: {
  scope?: 'today' | 'all_active';
  source?: string;
  category?: string;
  /** When true, server groups by (source × decision × category) and each
   *  cell carries a `category` field. */
  groupByCategory?: boolean;
  /** Time window in hours for `scope: 'today'`. Server clamps to
   *  [0.5, 720]. Ignored for `'all_active'`. */
  windowHours?: number;
} = {}) {
  const q = new URLSearchParams();
  if (opts.scope)    q.set('scope',    opts.scope);
  if (opts.source)   q.set('source',   opts.source);
  if (opts.category) q.set('category', opts.category);
  if (opts.groupByCategory) q.set('groupByCategory', '1');
  if (opts.windowHours != null) q.set('windowHours', String(opts.windowHours));
  const qs = q.toString();
  return request<DecisionsResp>(`/v1/observe/dbview/pipeline/decisions${qs ? '?' + qs : ''}`);
}

export function getPipelineDropReasons(opts: {
  sinceMs?: number; topReasons?: number; topSources?: number;
} = {}) {
  const q = new URLSearchParams();
  if (opts.sinceMs)    q.set('sinceMs',    String(opts.sinceMs));
  if (opts.topReasons) q.set('topReasons', String(opts.topReasons));
  if (opts.topSources) q.set('topSources', String(opts.topSources));
  const qs = q.toString();
  return request<DropReasonsResp>(`/v1/observe/dbview/pipeline/drop-reasons${qs ? '?' + qs : ''}`);
}

export function getPipelineSparkline(opts: { bucketHours?: number; buckets?: number } = {}) {
  const q = new URLSearchParams();
  if (opts.bucketHours) q.set('bucketHours', String(opts.bucketHours));
  if (opts.buckets)     q.set('buckets',     String(opts.buckets));
  const qs = q.toString();
  return request<PipelineSparklineResp>(`/v1/observe/dbview/pipeline/sparkline${qs ? '?' + qs : ''}`);
}

// ── DB Viewer power-features (Tab 6) ────────────────────────────────────────

export interface SchemaField { path: string; type: string; sampleValue: string | null }
export interface SchemaResp { ok: true; name: string; fields: SchemaField[] }

export type FilterOp =
  | 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'contains' | 'exists' | 'in';

export interface FilterRow {
  field: string;
  op: FilterOp;
  value: string | number | boolean | null;
}

export interface QueryReq {
  name: string;
  filterRows?: FilterRow[];
  limit?: number;
  projection?: string[];
  sort?: Record<string, 1 | -1>;
}

export interface QueryResp {
  ok: true;
  name: string;
  total: number;
  total_is_estimate: boolean;
  docs: Array<Record<string, unknown>>;
  took_ms: number;
}

export function getSchema(name: string) {
  return request<SchemaResp>(`/v1/observe/dbview/schema/${encodeURIComponent(name)}`);
}

export function runQuery(body: QueryReq) {
  return request<QueryResp>('/v1/observe/dbview/query', { method: 'POST', body });
}

// ── Scraped HTML — block signals (Tab 4 hardening) ──────────────────────────

export interface BlockSignals {
  html_bytes: number | null;
  small_html: boolean;
  title_block_match: string | null;
  parsed_listings: number;
  registry_status: 'ok' | 'blocked' | 'error' | 'never' | 'unknown';
  verdict: 'healthy' | 'partial_block' | 'hard_block' | 'unknown';
}

export interface ScrapedSignalsResp {
  ok: true;
  doc: Record<string, unknown>;
  signals: BlockSignals;
}

export function getScrapedDocSignals(id: string) {
  return request<ScrapedSignalsResp>(
    `/v1/observe/dbview/scraped/${encodeURIComponent(id)}/signals`,
  );
}

// ── Deal Explorer (Tab 2) ───────────────────────────────────────────────────

export type AdminDecision = 'EMIT' | 'HOLD' | 'DROP' | 'SUPPRESS';
export type AdminLiveStatus = 'live' | 'expired' | 'suspended' | 'oos';
/** 2026-05-10 — Tony calibration #4 dealTier band names. Server filter
 *  translates these to a discountPct range (≥40 / 25-39 / <25). Mirrors
 *  `deals.service.ts::dealTier()` on the backend. */
export type AdminDealTier = 'exceptional' | 'strong' | 'standard';
export type FlagReason =
  | 'wrong-DROP' | 'wrong-HOLD' | 'wrong-EMIT' | 'wrong-score' | 'wrong-category';

export interface AdminDealRow {
  _id: string;
  unique_id: string;
  source: string;
  title: string;
  price: number | null;
  url: string;
  category?: string;
  condition?: string;
  decision?: AdminDecision;
  dcs?: number;
  modelKey?: string;
  expired?: boolean;
  suspended?: boolean;
  outOfStock?: boolean;
  ts?: string;
  offer_seen_at?: string;
  /** 2026-05-10 — drives the dealTier emoji on the row (🔥/✓/•). May be
   *  absent for legacy rows scored before the field was populated. */
  discountPct?: number;
}

export interface AdminDealsFilter {
  source?: string;
  category?: string;
  decision?: AdminDecision;
  status?: AdminLiveStatus;
  q?: string;
  minDcs?: number; maxDcs?: number;
  minPrice?: number; maxPrice?: number;
  /** 2026-05-10 — server-side filter; translates to a discountPct range
   *  (≥40 / 25-39 / <25). Mirrors `deals.service.ts::dealTier()`. */
  dealTier?: AdminDealTier;
  since?: string;
  limit?: number;
  skip?: number;
}

export interface AdminDealsResp {
  ok: true;
  total: number;
  total_is_estimate: boolean;
  rows: AdminDealRow[];
}

function buildAdminDealsQs(f: AdminDealsFilter & { n?: number }): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== '' && v !== null) q.set(k, String(v));
  }
  return q.toString();
}

export function getAdminDeals(filter: AdminDealsFilter) {
  const qs = buildAdminDealsQs(filter);
  return request<AdminDealsResp>(`/v1/observe/dbview/admin-deals${qs ? '?' + qs : ''}`);
}

export function sampleAdminDeals(filter: AdminDealsFilter & { n?: number }) {
  const qs = buildAdminDealsQs(filter);
  return request<{ ok: true; rows: AdminDealRow[] }>(
    `/v1/observe/dbview/admin-deals/sample${qs ? '?' + qs : ''}`,
  );
}

export function getAdminDeal(uid: string) {
  return request<{ ok: true; doc: Record<string, unknown> }>(
    `/v1/observe/dbview/admin-deals/${encodeURIComponent(uid)}`,
  );
}

export interface PipelineLogEntry {
  _id: string;
  ts: string;
  stage: string;
  decision: string;
  primary_reason?: string;
  secondary_reasons?: string[];
  dcs?: number;
}

export function getDealPipelineLog(uid: string) {
  return request<{ ok: true; entries: PipelineLogEntry[] }>(
    `/v1/observe/dbview/admin-deals/${encodeURIComponent(uid)}/pipeline-log`,
  );
}

export interface DeliveryEntry {
  _id: string;
  tgId: number;
  sent_at: string;
  source?: string;
  channel?: string;
}

export function getDealDeliveries(uid: string) {
  return request<{ ok: true; entries: DeliveryEntry[] }>(
    `/v1/observe/dbview/admin-deals/${encodeURIComponent(uid)}/deliveries`,
  );
}

export interface FlagRecord {
  _id: string;
  ts: string;
  unique_id: string;
  source?: string;
  modelKey?: string;
  category?: string;
  current_decision?: string;
  reason: FlagReason;
  note?: string;
  flagged_by: string;
}

export function flagDeal(uid: string, body: { reason: FlagReason; note?: string }) {
  return request<{ ok: true; flag: FlagRecord }>(
    `/v1/observe/dbview/admin-deals/${encodeURIComponent(uid)}/flag`,
    { method: 'POST', body },
  );
}

export function listFlags(opts: { since?: string; reason?: FlagReason; unique_id?: string; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (opts.since)     q.set('since', opts.since);
  if (opts.reason)    q.set('reason', opts.reason);
  if (opts.unique_id) q.set('unique_id', opts.unique_id);
  if (opts.limit)     q.set('limit', String(opts.limit));
  const qs = q.toString();
  return request<{ ok: true; flags: FlagRecord[] }>(
    `/v1/observe/dbview/admin-deals/flags${qs ? '?' + qs : ''}`,
  );
}

export interface FlagAggregateResp {
  ok: true;
  by_model_key: Array<{ modelKey: string; reason: FlagReason; n: number }>;
  by_source:    Array<{ source: string;   reason: FlagReason; n: number }>;
}

export function aggregateFlags(opts: { sinceMs?: number } = {}) {
  const q = new URLSearchParams();
  if (opts.sinceMs) q.set('sinceMs', String(opts.sinceMs));
  const qs = q.toString();
  return request<FlagAggregateResp>(
    `/v1/observe/dbview/admin-deals/flags/aggregate${qs ? '?' + qs : ''}`,
  );
}

// ─── Anchor formula config (Tony's weighted blend) ─────────────────────────

export type AnchorFormulaMode = 'legacy' | 'weighted';

export interface AnchorFormulaConfig {
  mode: AnchorFormulaMode;
  weights: { market: number; used: number; retail: number };
  ebaySoldOutlierIqrK: number;
  minEbaySoldSales: number;
  updatedAt?: string;
  updatedBy?: number | null;
}

export interface AnchorConfigResp {
  ok: true;
  config: AnchorFormulaConfig;
  defaults: AnchorFormulaConfig;
}

export function getAnchorConfig() {
  return request<AnchorConfigResp>('/v1/observe/dbview/anchor-config');
}

export function patchAnchorConfig(
  patch: Partial<AnchorFormulaConfig> & { updatedBy?: number | null },
) {
  return request<{ ok: true; config: AnchorFormulaConfig }>(
    '/v1/observe/dbview/anchor-config',
    { method: 'PATCH', body: patch },
  );
}

// ─── DCS scoring rules (2026-05-15 — editable knobs + read-only snapshot) ──

export interface QualityConfigPayload {
  phoneStructuralDamagePenalty: number;
  phoneStructuralDamageHardDrop: boolean;
  // 2026-05-16 — category-wise gates (Sumit's "category min thresholds" ask).
  categoryMinSaving:   Record<string, number>;
  categoryMinDiscount: Record<string, number>;
  categoryHardDrop:    Record<string, boolean>;
  // 2026-05-17 — DCS decision thresholds (live-tunable). Defaults 70/60.
  // Validation server-side: 0 ≤ hold < emit ≤ 100.
  dcsEmitThreshold: number;
  dcsHoldThreshold: number;
  // 2026-05-18 — DCS image-card threshold (Reseller-Pro PNG cards).
  // Range 0–101 (101 = effectively off). Default 90.
  dcsImageCardThreshold: number;
  // 2026-07-03 — Option B anchor-miss fallback: kill switch + sold-price guard.
  fallbackEmitEnabled?: boolean;
  fallbackRequireBelowSoldPct?: number;
  // 2026-10-23 — hold queue for disagreements only (ships dark).
  holdQueueDisagreementsOnly?: boolean;
  updatedAt?: string;
  updatedBy?: number | null;
}

export interface ScoringRulesSummary {
  editable: {
    phoneStructuralDamagePenalty: number;
    phoneStructuralDamageHardDrop: boolean;
    categoryMinSaving:   Record<string, number>;
    categoryMinDiscount: Record<string, number>;
    categoryHardDrop:    Record<string, boolean>;
  };
  dcsThresholds: {
    emit: number;
    hold: number;
    imageCard: number;
    priceScoreMin: number;
    maxQualityScore: number;
    minQualityScore: number;
  };
  trustTiers: {
    full: string[];
    retail: string[];
    graded: string[];
    noneExamples: string[];
  };
  qualityPenalties: Array<{
    name: string;
    magnitude: number;
    category: 'universal' | 'phones' | 'consoles' | 'tablets' | 'laptops' | 'gpus' | 'other';
    note?: string;
  }>;
  conditionSets: {
    good: string[];
    fair: string[];
    bad:  string[];
  };
  regexPatterns: Array<{
    name: string;
    pattern: string;
    description: string;
  }>;
  categoryRules: Array<{
    category: string;
    rule: string;
    value: string | number | null;
  }>;
}

export interface ScoringRulesResp {
  ok: true;
  editableConfig: QualityConfigPayload;
  defaults: QualityConfigPayload;
  rulesSummary: ScoringRulesSummary;
}

export function getScoringRules() {
  return request<ScoringRulesResp>('/v1/observe/dbview/scoring-rules');
}

export function patchScoringRules(
  patch: Partial<QualityConfigPayload> & { updatedBy?: number | null },
) {
  return request<{ ok: true; config: QualityConfigPayload }>(
    '/v1/observe/dbview/scoring-rules',
    { method: 'PATCH', body: patch },
  );
}

// ─── Image-card metrics (2026-05-18, Reseller PNG renderer health) ──────────

export interface ImageCardMetrics {
  windowHours: number;
  total: number;
  perHour: number;
  byOutcome: {
    cache_hit: number;
    rendered: number;
    failed_no_font: number;
    failed_render: number;
  };
  cacheHitRate: number;
  imageFetchOkRate: number | null;
  duration: {
    coldRenders: number;
    p50Ms: number | null;
    p95Ms: number | null;
    p99Ms: number | null;
    meanMs: number | null;
  };
  pngBytes: {
    coldRenders: number;
    meanBytes: number | null;
  };
  lastFailure: { ts: string; errorMessage: string } | null;
}

export function getImageCardMetrics(windowHours: number = 24) {
  return request<{ ok: true; snapshot: ImageCardMetrics }>(
    `/v1/observe/dbview/image-card-metrics?windowHours=${windowHours}`,
  );
}

// ─── Emit latency (2026-05-18, scrape-cadence-to-emit observability) ────────

export interface EmitLatencyPercentiles {
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  meanMs: number | null;
}

export interface EmitLatencyMetrics {
  windowHours: number;
  total: number;
  overall: EmitLatencyPercentiles;
  bySource: Array<{ source: string; count: number } & EmitLatencyPercentiles>;
}

export function getEmitLatencyMetrics(windowHours: number = 24) {
  return request<{ ok: true; snapshot: EmitLatencyMetrics }>(
    `/v1/observe/dbview/emit-latency?windowHours=${windowHours}`,
  );
}

// ─── Delivery latency (2026-06-07, emit→delivery user-facing latency) ───────

export interface DeliveryLatencyPercentiles {
  p50Ms: number | null;
  p90Ms: number | null;
  p95Ms: number | null;
  meanMs: number | null;
}

export interface DeliveryLatencyMetrics {
  windowHours: number;
  totalJoined: number;
  freshCount: number;
  redeliveryCount: number;
  noStampCount: number;
  fresh: DeliveryLatencyPercentiles;
  byTier: Array<{ tier: string; count: number } & DeliveryLatencyPercentiles>;
  freshBuckets: {
    under1m: number; m1to5: number; m5to30: number; m30to60: number; over1h: number;
  };
  targetFreeMs: number;
  targetProMs: number;
}

export function getDeliveryLatencyMetrics(windowHours: number = 24) {
  return request<{ ok: true; snapshot: DeliveryLatencyMetrics }>(
    `/v1/observe/dbview/delivery-latency?windowHours=${windowHours}`,
  );
}

// ─── User management (Wave 3) ──────────────────────────────────────────────

export interface AdminUserListRow {
  tgId: number;
  username: string | null;
  firstName: string | null;
  tier: string | null;
  role: string | null;
  dm_opt_in: boolean;
  prefs: {
    categories: string[];
    budget: { id?: string; label?: string; min?: number; max?: number } | null;
    dm_frequency: string | null;
    onboardingComplete: boolean;
    city: string | null;
  };
  createdAt: string | null;
  updatedAt: string | null;
  lastDeliveryAt: string | null;
  deliveryCount: number;
  feedbackCount30d: number;
}

export interface AdminUserDetail extends AdminUserListRow {
  fullPrefs: any;
  signals: {
    likes:    { sources: Record<string, number>; categories: Record<string, number> };
    dislikes: { sources: Record<string, number>; categories: Record<string, number> };
    meh:      { sources: Record<string, number>; categories: Record<string, number> };
    hides:    { sources: Record<string, number>; deals: number };
    clicks:   { sources: Record<string, number>; categories: Record<string, number> };
  };
  recentDeliveries: Array<{
    sent_at: string;
    deal_id: string;
    source: string | null;
    category: string | null;
    deal_price: number | null;
    dcs_score: number | null;
    model_key: string | null;
    tier: string | null;
  }>;
  recentEvents: Array<{ ts: string; type: string; meta: any }>;
  badges: Array<{ badgeId: string; awardedAt: string | null }>;
  preferenceVector: any | null;
}

export interface AdminUserListResp {
  ok: true;
  rows: AdminUserListRow[];
  total: number;
}

export function listAdminUsers(opts: {
  q?: string;
  tier?: string;
  dmOptIn?: boolean;
  onboarded?: boolean;
  deliveredWithinDays?: number;
  limit?: number;
  skip?: number;
} = {}) {
  const p = new URLSearchParams();
  if (opts.q !== undefined) p.set('q', opts.q);
  if (opts.tier) p.set('tier', opts.tier);
  if (opts.dmOptIn !== undefined) p.set('dmOptIn', String(opts.dmOptIn));
  if (opts.onboarded !== undefined) p.set('onboarded', String(opts.onboarded));
  if (opts.deliveredWithinDays !== undefined) p.set('deliveredWithinDays', String(opts.deliveredWithinDays));
  if (opts.limit !== undefined) p.set('limit', String(opts.limit));
  if (opts.skip !== undefined) p.set('skip', String(opts.skip));
  const qs = p.toString();
  return request<AdminUserListResp>(`/v1/observe/dbview/users${qs ? '?' + qs : ''}`);
}

export function getAdminUser(tgId: number) {
  return request<{ ok: true } & AdminUserDetail>(`/v1/observe/dbview/users/${tgId}`);
}

// ── Per-user price-aware cooldown viewer (Tony calibration #2) ──────────────

/** One per-user cooldown record from Redis (`cooldown:user:${tgId}:*`).
 *  Read-only — the dashboard does not delete or modify cooldown state. */
export interface UserCooldownRecord {
  modelKey:    string;
  priceAtSend: number;
  ttlSeconds:  number;
  /** ISO 8601 — derived server-side from ttlSeconds + now at fetch time. */
  expiresAt:   string;
}

export function getUserCooldowns(tgId: number) {
  return request<{ ok: true; records: UserCooldownRecord[] }>(
    `/v1/observe/dbview/users/${tgId}/cooldowns`,
  );
}
